import { useState, useEffect, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';
import {
    Button,
    TextField,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    IconButton,
    CircularProgress,
    Alert,
    Modal,
    Fade,
} from '@mui/material';
import { Search, KeyboardArrowLeft, KeyboardArrowRight, Close } from '@mui/icons-material';

import TransactionSelectionLoading from './TransactionSelectionLoading';

import { BankAccount } from 'services/hurdlr/queries/useHurdlrPlaidItems/useHurdlrPlaidItems.interface';
import collectiveApi from 'modules/common/collectiveApi';
import BankLogo from 'modules/HubV2/Bookkeeping/components/Banks2/BankList/Bank/BankLogo';
import { moneyFormat } from 'modules/common/display';
import theme from 'theme';

interface Transaction {
    id: string;
    entity_id: number;
    amount: string;
    description: string;
    date: string;
    type?: string;
    accountId: number;
}

// Union type to handle both BankAccount and PersonalAccount
type AccountType =
    | BankAccount
    | {
          id: string;
          name: string;
          type: string;
          lastFour: string;
          apiAccountId?: string;
      };

interface TransactionSelectionModalProps {
    open: boolean;
    onClose: () => void;
    account?: AccountType;
    // Shared state from parent component
    setAccountSelections: (accountId: string, transactions: Transaction[]) => void;
    getAccountSelections: (accountId: string) => Transaction[];
}

function TransactionSelectionModal({
    open,
    onClose,
    account = undefined,
    setAccountSelections,
    getAccountSelections,
}: TransactionSelectionModalProps) {
    // All useState declarations at the top
    const [localSelections, setLocalSelections] = useState<{ [accountId: string]: Transaction[] }>({});
    const [originalSelections, setOriginalSelections] = useState<{ [accountId: string]: Transaction[] }>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState<{
        cursor?: string;
        hasMore: boolean;
        total?: number;
    }>({ hasMore: false });
    const [isSearchMode, setIsSearchMode] = useState(false);

    // Initialize local selections when modal opens
    useEffect(() => {
        if (open && account) {
            const plaidAccountId = account?.apiAccountId || account?.id;
            if (plaidAccountId) {
                const currentSelections = getAccountSelections(String(plaidAccountId));
                const accountId = String(plaidAccountId);
                setLocalSelections({
                    [accountId]: currentSelections,
                });
                setOriginalSelections({
                    [accountId]: currentSelections,
                });
            }
        } else if (!open) {
            // Reset when modal closes
            setLocalSelections({});
            setOriginalSelections({});
            // Reset search state
            setSearchTerm('');
            setDebouncedSearchTerm('');
            setCurrentPage(1);
            setIsSearchMode(false);
            setTransactions([]);
            setPagination({ hasMore: false });
            setLoading(false);
            setError(null);
        }
    }, [open, account, getAccountSelections]);

    // ✅ Phase 4: O(1) transaction selection check - Use Plaid ID consistently
    // Remove the old isTransactionSelected function - using local version instead

    // Debounced search effect
    const debouncedSearch = useMemo(
        () =>
            debounce((searchValue: string) => {
                setDebouncedSearchTerm(searchValue);
            }, 300),
        []
    );

    useEffect(() => {
        debouncedSearch(searchTerm);
        return () => {
            debouncedSearch.cancel();
        };
    }, [searchTerm, debouncedSearch]);

    const transactionsPerPage = 50; // Increased from 25 to 50

    const fetchTransactions = useCallback(
        async (reset = false, useSearch: boolean = isSearchMode, cursor?: string) => {
            // Debug logging removed

            // Handle both BankAccount (apiAccountId) and PersonalAccount (id as Plaid ID) objects
            const plaidAccountId = account?.apiAccountId || account?.id;

            if (!plaidAccountId) {
                // No Plaid account ID found, returning early
                return;
            }

            setError(null);
            setLoading(true);

            try {
                let url: string;
                let params: URLSearchParams;

                // Use Plaid ID consistently for API calls
                const accountIdentifier = plaidAccountId;

                if (useSearch && debouncedSearchTerm.trim()) {
                    // Use search endpoint with plaidAccountId
                    url = `/bookkeeping/personal-bank-account/${accountIdentifier}/search-transactions/`;
                    params = new URLSearchParams({
                        q: debouncedSearchTerm.trim(),
                        limit: transactionsPerPage.toString(),
                    });
                } else {
                    // Use regular paginated endpoint with plaidAccountId
                    url = `/bookkeeping/personal-bank-account/${accountIdentifier}/transactions/`;
                    params = new URLSearchParams({
                        limit: transactionsPerPage.toString(),
                    });
                }

                if (cursor) {
                    params.append('cursor', cursor);
                }

                const response = await collectiveApi.get(`${url}?${params.toString()}`);

                if (response.data.success) {
                    const fetchedTransactions = response.data.data.transactions.map(
                        (t: Record<string, unknown>, index: number) => ({
                            ...t,
                            id: t.id || `transaction-${t.entity_id || index}-${Date.now()}`,
                            accountId: accountIdentifier, // Use Plaid ID consistently
                        })
                    );

                    if (reset) {
                        setTransactions(fetchedTransactions);
                        setCurrentPage(1); // Reset to first page only when replacing data
                    } else {
                        // Append for pagination - advance to show new data
                        setTransactions((prev) => {
                            const newTransactions = [...prev, ...fetchedTransactions];
                            // Calculate which page should show the new data
                            const newPageForNewData = Math.ceil(prev.length / transactionsPerPage) + 1;
                            setCurrentPage(newPageForNewData);
                            return newTransactions;
                        });
                    }

                    setPagination((prev) => {
                        const newPagination = {
                            cursor: response.data.data.pagination?.cursor,
                            hasMore: response.data.data.pagination?.hasMore,
                            // Preserve total from first load, only update if new total is provided (not null or undefined)
                            total:
                                response.data.data.pagination?.total != null
                                    ? response.data.data.pagination.total
                                    : prev.total,
                        };

                        return newPagination;
                    });
                } else {
                    setError('Failed to load transactions');
                }
            } catch (err) {
                setError('Failed to load transactions. Please try again.');
            } finally {
                setLoading(false);
            }
        },
        [account, debouncedSearchTerm, isSearchMode, transactionsPerPage]
    );

    // ✅ Phase 1: Simplified - no need to load selections, Layer 3 handles persistence
    useEffect(() => {
        const plaidAccountId = account?.apiAccountId || account?.id;
        if (open && plaidAccountId) {
            fetchTransactions(true);
        }
    }, [open, account?.apiAccountId, account?.id, fetchTransactions]);

    // Handle search term changes (skip initial load when search is empty)
    useEffect(() => {
        const plaidAccountId = account?.apiAccountId || account?.id;
        if (open && plaidAccountId && debouncedSearchTerm !== undefined) {
            const shouldUseSearch = debouncedSearchTerm.trim().length > 0;

            // Only fetch if we have a search term (not on initial empty load)
            if (shouldUseSearch) {
                setCurrentPage(1);
                setIsSearchMode(true);
                fetchTransactions(true, true);
            } else if (isSearchMode) {
                // Clear search - go back to browse mode
                setCurrentPage(1);
                setIsSearchMode(false);
                fetchTransactions(true, false);
            }
        }
    }, [debouncedSearchTerm, open, account?.apiAccountId, account?.id, fetchTransactions, isSearchMode]);

    // Local selection functions (not persisted until Save) - moved up to avoid use-before-define
    const isTransactionSelectedLocally = useCallback(
        (transaction: Transaction): boolean => {
            const plaidAccountId = account?.apiAccountId || account?.id;
            if (!plaidAccountId) {
                return false;
            }

            const accountSelections = localSelections[String(plaidAccountId)] || [];
            return accountSelections.some((t) => t.entity_id === transaction.entity_id);
        },
        [localSelections, account]
    );

    // Show all transactions (no filtering by selection status)
    const filteredTransactions = transactions;
    // Use local array length for pagination calculations (what's actually loaded)
    const localTransactionCount = filteredTransactions.length;
    const totalPages = Math.ceil(localTransactionCount / transactionsPerPage);
    const startIndex = (currentPage - 1) * transactionsPerPage;
    const endIndex = startIndex + transactionsPerPage;
    const currentTransactions = filteredTransactions.slice(startIndex, endIndex);

    // Use backend total count for display purposes only
    const totalTransactions = pagination.total ?? localTransactionCount;

    // Smart pagination: Next button either navigates pages or loads more data
    const handleNextPage = () => {
        if (currentPage < totalPages) {
            // Navigate to next local page
            setCurrentPage((prev) => prev + 1);
        } else if (pagination.hasMore && !loading) {
            // Load more transactions from backend
            fetchTransactions(false, isSearchMode, pagination.cursor);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    // Determine if Next button should be enabled
    const canGoNext = currentPage < totalPages || pagination.hasMore;

    const getLocalAccountSelections = useCallback(
        (accountId: string): Transaction[] => {
            return localSelections[accountId] || [];
        },
        [localSelections]
    );

    // Check if there are changes to save (compare local vs original selections)
    const hasChangesToSave = useCallback((): boolean => {
        const plaidAccountId = account?.apiAccountId || account?.id;
        if (!plaidAccountId) {
            return false;
        }

        const accountId = String(plaidAccountId);
        const localSelectionsForAccount = localSelections[accountId] || [];
        const originalSelectionsForAccount = originalSelections[accountId] || [];

        // Compare by entity_id sets
        const localEntityIds = new Set(localSelectionsForAccount.map((t) => t.entity_id));
        const originalEntityIds = new Set(originalSelectionsForAccount.map((t) => t.entity_id));

        // Check if sets are different
        if (localEntityIds.size !== originalEntityIds.size) {
            return true;
        }

        return Array.from(localEntityIds).some((entityId) => !originalEntityIds.has(entityId));
    }, [localSelections, originalSelections, account]);

    const handleToggleTransaction = (transaction: Transaction) => {
        const plaidAccountId = account?.apiAccountId || account?.id;
        if (!plaidAccountId) {
            return;
        }

        const accountId = String(plaidAccountId);
        const currentSelections = localSelections[accountId] || [];
        const exists = currentSelections.some((t) => t.entity_id === transaction.entity_id);

        setLocalSelections((prev) => {
            if (exists) {
                // Remove transaction
                const filtered = currentSelections.filter((t) => t.entity_id !== transaction.entity_id);
                return {
                    ...prev,
                    [accountId]: filtered,
                };
            }
            // Add transaction
            return {
                ...prev,
                [accountId]: [...currentSelections, transaction],
            };
        });
    };

    // ✅ Phase 1: Bulk selection using local state - Use Plaid ID
    const handleSelectAll = () => {
        const plaidAccountId = account?.apiAccountId || account?.id;
        if (!plaidAccountId) {
            return;
        }

        const accountId = String(plaidAccountId);
        const allSelected = currentTransactions.every((t) => isTransactionSelectedLocally(t));
        const someSelected = currentTransactions.some((t) => isTransactionSelectedLocally(t));

        setLocalSelections((prev) => {
            const currentSelections = prev[accountId] || [];

            // If all are selected OR some are selected (indeterminate state), deselect all
            if (allSelected || someSelected) {
                // Deselect all current page transactions
                const currentPageEntityIds = new Set(currentTransactions.map((t) => t.entity_id));
                const filtered = currentSelections.filter((t) => !currentPageEntityIds.has(t.entity_id));
                return {
                    ...prev,
                    [accountId]: filtered,
                };
            }
            // If none are selected, select all current page transactions
            const existingEntityIds = new Set(currentSelections.map((t) => t.entity_id));
            const newTransactions = currentTransactions.filter((t) => !existingEntityIds.has(t.entity_id));
            return {
                ...prev,
                [accountId]: [...currentSelections, ...newTransactions],
            };
        });
    };

    // Save local selections to persistent storage and close modal
    const handleConfirm = () => {
        const plaidAccountId = account?.apiAccountId || account?.id;
        if (plaidAccountId) {
            const accountId = String(plaidAccountId);
            const selectionsToSave = localSelections[accountId] || [];

            setAccountSelections(accountId, selectionsToSave);
        }
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}>
            <Fade in={open}>
                <Box
                    sx={{
                        width: '100%',
                        // Responsive modal sizing
                        maxWidth: { xs: '95vw', sm: '90vw', md: theme.spacing(150), lg: theme.spacing(175) }, // 1200px = 150 * 8px, 1400px = 175 * 8px
                        height: { xs: '90vh', sm: '80vh' },
                        backgroundColor: 'common.white',
                        boxShadow: 24,
                        borderRadius: 2,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                    {/* Header Section */}
                    <Box
                        sx={{
                            // Responsive header height and padding
                            height: { xs: 'auto', sm: 72 },
                            p: { xs: 2, sm: '20px 24px 20px 16px' },
                            borderBottom: 1,
                            borderColor: 'grey.100',
                            display: 'flex',
                            flexDirection: 'row', // Always horizontal layout
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: { xs: 1, sm: 0 },
                        }}>
                        {/* Left side: Bank logo and account info */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 1, sm: 2 },
                                flex: 1,
                                minWidth: 0, // Allow text to truncate if needed
                            }}>
                            <BankLogo
                                institutionId={
                                    account && 'institutionId' in account ? String(account.institutionId) : ''
                                }
                                width={32}
                                height={32}
                                backgroundColor="#2A5097"
                            />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                    variant="subtitle1"
                                    color="text.primary"
                                    sx={{
                                        marginBottom: { xs: 0.5, sm: 0.25 },
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>
                                    {account && 'apiAccountName' in account
                                        ? account.apiAccountName
                                        : account?.name || 'Account'}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        overflow: 'hidden',
                                        marginTop: { xs: 0.25, sm: 0 },
                                    }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {account && 'apiAccountType' in account
                                            ? account.apiAccountType
                                            : account?.type}
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 3,
                                            height: 3,
                                            backgroundColor: 'text.secondary',
                                            borderRadius: '50%',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {account && 'apiAccountNo' in account
                                            ? account.apiAccountNo
                                            : account?.lastFour}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Right side: Close button */}
                        <IconButton
                            onClick={onClose}
                            sx={{
                                // Responsive close button sizing
                                width: { xs: 28, sm: 32 },
                                height: { xs: 28, sm: 32 },
                                backgroundColor: 'cement.100',
                                borderRadius: '50%',
                                flexShrink: 0, // Prevent button from shrinking
                                '&:hover': {
                                    backgroundColor: 'cement.200',
                                },
                            }}>
                            <Close
                                sx={{
                                    width: { xs: 14, sm: 16 },
                                    height: { xs: 14, sm: 16 },
                                    color: 'text.primary',
                                }}
                            />
                        </IconButton>
                    </Box>

                    {/* Main Content Section */}
                    <Box
                        sx={{
                            flex: 1,
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                            minHeight: 0, // Important: allows flex child to shrink
                            overflow: 'hidden', // Prevent content from overflowing
                        }}>
                        {loading ? (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                <TransactionSelectionLoading
                                    title="Hang tight, loading your transactions"
                                    description="This may take a few minutes."
                                />
                            </Box>
                        ) : (
                            <>
                                {/* Title */}
                                <Typography variant="h5" color="text.primary">
                                    Select business transactions you&apos;d like to import
                                </Typography>

                                {/* Search and Filters */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        justifyContent: 'space-between',
                                        alignItems: { xs: 'stretch', sm: 'center' },
                                        gap: { xs: 2, sm: 0 },
                                    }}>
                                    {/* Left side: Search and Filter together */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {/* Search Input */}
                                        <TextField
                                            placeholder="Search"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            sx={{
                                                // Responsive search input width
                                                width: { xs: '100%', sm: 300, md: 400, lg: 500 },
                                                maxWidth: { xs: '100%', sm: 'none' },
                                                '& .MuiOutlinedInput-root': {
                                                    height: 'auto',
                                                    borderRadius: 1,
                                                    p: '8px 16px',
                                                    outline: 1,
                                                    outlineColor: 'text.secondary',
                                                    outlineOffset: '-1px',
                                                    border: 'none',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    '& fieldset': {
                                                        border: 'none',
                                                    },
                                                    '&:hover fieldset': {
                                                        border: 'none',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        border: 'none',
                                                    },
                                                },
                                                '& .MuiInputBase-input': {
                                                    padding: 0,
                                                    flex: '1 1 0',
                                                    '&::placeholder': {
                                                        color: 'text.secondary',
                                                        opacity: 1,
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            gap: 1.25,
                                                        }}>
                                                        <Search
                                                            sx={{
                                                                width: 16,
                                                                height: 16,
                                                                color: 'text.secondary',
                                                                position: 'relative',
                                                            }}
                                                        />
                                                    </Box>
                                                ),
                                                endAdornment:
                                                    loading && isSearchMode ? <CircularProgress size={20} /> : null,
                                            }}
                                        />
                                    </Box>

                                    {/* Right side: Pagination info */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {startIndex + 1}–{Math.min(endIndex, totalTransactions)} of{' '}
                                            {totalTransactions}
                                        </Typography>
                                        <IconButton
                                            onClick={handlePreviousPage}
                                            disabled={currentPage === 1}
                                            size="small"
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                p: 0.5,
                                                '&:hover': {
                                                    backgroundColor: 'grey.100',
                                                },
                                            }}>
                                            <KeyboardArrowLeft
                                                sx={{
                                                    width: 16,
                                                    height: 16,
                                                    color: currentPage === 1 ? 'grey.300' : 'text.secondary',
                                                }}
                                            />
                                        </IconButton>
                                        <IconButton
                                            onClick={handleNextPage}
                                            disabled={!canGoNext || loading}
                                            size="small"
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                p: 0.5,
                                                '&:hover': {
                                                    backgroundColor: 'grey.100',
                                                },
                                            }}>
                                            {loading && currentPage >= totalPages && pagination.hasMore ? (
                                                <CircularProgress size={16} />
                                            ) : (
                                                <KeyboardArrowRight
                                                    sx={{
                                                        width: 16,
                                                        height: 16,
                                                        color: !canGoNext || loading ? 'grey.300' : 'text.secondary',
                                                    }}
                                                />
                                            )}
                                        </IconButton>
                                    </Box>
                                </Box>

                                {/* Transaction Table */}
                                <TableContainer
                                    component={Paper}
                                    sx={{
                                        boxShadow: 'none',
                                        flex: 1, // Take all available space in the main content area
                                        overflow: 'auto',
                                        minHeight: 0, // Important: allows flex child to shrink
                                        // Responsive table scrolling
                                        '&::-webkit-scrollbar': {
                                            height: { xs: 8, sm: 6 },
                                        },
                                    }}>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell
                                                    padding="checkbox"
                                                    sx={{
                                                        width: 60,
                                                        backgroundColor: 'cement.50',
                                                        borderBottom: 1,
                                                        borderColor: 'grey.200',
                                                        p: '8px 16px', // Explicit padding to match transaction cells
                                                        verticalAlign: 'middle',
                                                    }}>
                                                    <Checkbox
                                                        checked={
                                                            currentTransactions.length > 0 &&
                                                            currentTransactions.every((t) =>
                                                                isTransactionSelectedLocally(t)
                                                            )
                                                        }
                                                        indeterminate={
                                                            currentTransactions.some((t) =>
                                                                isTransactionSelectedLocally(t)
                                                            ) &&
                                                            !currentTransactions.every((t) =>
                                                                isTransactionSelectedLocally(t)
                                                            )
                                                        }
                                                        onChange={handleSelectAll}
                                                        sx={{
                                                            padding: 0, // Remove default checkbox padding
                                                        }}
                                                        size="small"
                                                        color="secondary"
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        // Responsive date column width
                                                        width: { xs: 80, md: 100 },
                                                        minWidth: { xs: 70, sm: 80 },
                                                        backgroundColor: 'cement.50',
                                                        borderBottom: 1,
                                                        borderColor: 'grey.200',
                                                    }}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        fontWeight={700}
                                                        sx={{
                                                            textTransform: 'uppercase',
                                                        }}>
                                                        DATE
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        backgroundColor: 'cement.50',
                                                        borderBottom: 1,
                                                        borderColor: 'grey.200',
                                                    }}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        fontWeight={700}
                                                        sx={{
                                                            textTransform: 'uppercase',
                                                        }}>
                                                        DESCRIPTION
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        // Responsive amount column width
                                                        width: { xs: 120, md: 140 },
                                                        minWidth: { xs: 100, sm: 120 },
                                                        textAlign: 'right',
                                                        backgroundColor: 'cement.50',
                                                        borderBottom: 1,
                                                        borderColor: 'grey.200',
                                                    }}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        fontWeight={700}
                                                        sx={{
                                                            textTransform: 'uppercase',
                                                        }}>
                                                        AMOUNT
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(() => {
                                                if (error) {
                                                    return (
                                                        <TableRow>
                                                            <TableCell colSpan={4} align="center">
                                                                <Alert severity="error">{error}</Alert>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                }
                                                if (currentTransactions.length === 0) {
                                                    return (
                                                        <TableRow>
                                                            <TableCell colSpan={4} align="center">
                                                                No transactions found.
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                }
                                                return currentTransactions.map((transaction) => (
                                                    <TableRow
                                                        key={`${transaction.entity_id}-${transaction.id || 'no-id'}`}
                                                        hover
                                                        onClick={() => handleToggleTransaction(transaction)}
                                                        sx={{
                                                            backgroundColor: isTransactionSelectedLocally(transaction)
                                                                ? 'cement.50'
                                                                : 'inherit',
                                                            '&:hover': {
                                                                backgroundColor: isTransactionSelectedLocally(
                                                                    transaction
                                                                )
                                                                    ? 'cement.100'
                                                                    : 'grey.50',
                                                            },
                                                        }}>
                                                        <TableCell
                                                            padding="checkbox"
                                                            sx={{
                                                                p: '8px 16px', // Match header padding
                                                                verticalAlign: 'middle',
                                                            }}>
                                                            <Checkbox
                                                                checked={isTransactionSelectedLocally(transaction)}
                                                                onChange={() => handleToggleTransaction(transaction)}
                                                                sx={{
                                                                    padding: 0, // Remove default checkbox padding
                                                                }}
                                                                size="small"
                                                                color="secondary"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="subtitle2" color="text.secondary">
                                                                {new Date(transaction.date).toLocaleDateString(
                                                                    'en-US',
                                                                    {
                                                                        month: 'short',
                                                                        day: 'numeric',
                                                                    }
                                                                )}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    color: isTransactionSelectedLocally(transaction)
                                                                        ? 'text.primary'
                                                                        : 'text.secondary',
                                                                    wordWrap: 'break-word',
                                                                    whiteSpace: 'normal',
                                                                }}>
                                                                {transaction.description}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>
                                                            <Typography variant="subtitle2" color="text.primary">
                                                                {moneyFormat(parseFloat(transaction.amount))}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ));
                                            })()}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}
                    </Box>

                    {/* Footer Section */}
                    <Box
                        sx={{
                            p: 2,
                            px: 3,
                            backgroundColor: 'common.white',
                            borderTop: 1,
                            borderColor: 'grey.100',
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            gap: { xs: 2, sm: 0 },
                        }}>
                        {/* Left side: Selection summary */}
                        <Box sx={{ flex: 1 }}>
                            {(() => {
                                const plaidAccountId = account?.apiAccountId || account?.id;
                                const selectedTransactions = plaidAccountId
                                    ? getLocalAccountSelections(String(plaidAccountId))
                                    : [];
                                const selectedCount = selectedTransactions.length;

                                if (selectedCount > 0) {
                                    const totalAmount = selectedTransactions.reduce((sum, transaction) => {
                                        const amount = parseFloat(transaction.amount) || 0;
                                        return sum + Math.abs(amount); // Use absolute value for expenses
                                    }, 0);

                                    const formatCurrencyLocal = (amount: number): string => {
                                        return new Intl.NumberFormat('en-US', {
                                            style: 'currency',
                                            currency: 'USD',
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }).format(amount);
                                    };

                                    return (
                                        <Typography variant="subtitle2" color="text.primary">
                                            {selectedCount} transaction{selectedCount !== 1 ? 's' : ''} selected •{' '}
                                            {formatCurrencyLocal(totalAmount)}
                                        </Typography>
                                    );
                                }
                                return null;
                            })()}
                        </Box>

                        {/* Right side: Save button - always positioned on the right */}
                        <Button
                            onClick={handleConfirm}
                            disabled={!hasChangesToSave()}
                            variant="contained"
                            sx={{
                                // Responsive save button sizing
                                width: { xs: '100%', sm: 120 },
                                height: { xs: 44, sm: 40 },
                                px: 2.5, // 20px = 2.5 * 8px
                                py: 1.25, // 10px = 1.25 * 8px
                                borderRadius: 1.5,
                            }}>
                            Save
                        </Button>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
}

export default TransactionSelectionModal;
